import api from "./api";

export interface ReportLineItem {
    name: string;
    amount: number | string;
    status?: string;
}

export interface ReportEmployeeItem {
    name: string;
    amount: number | string;
    total_sales?: number;
    commission?: number | string;
}

export interface ZReportResponse {
    id: number;
    date: string;
    no_of_sales: number;
    total_sale_amount: string;
    products_total_amount: string;
    services_total_amount: string;
    total_tender_amount: string;
    total_quickPayments: string;
}

export interface ZReportsListResponse {
    reports: ZReportResponse[];
}

export interface ZReportDetailResponse {
    report: {
        id: number;
        company_info: Array<{
            id: number;
            name: string;
            address: string;
        }>;
        begin: string;
        end: string;
        requester: number;
        close_out: number;
        no_of_sales: number;
        note: string;
        services_total_amount: number;
        products_total_amount: number;
        total_sale_amount: number;
        total_quickpayments: string;
        total_discount: number;
        total_tax_amount: number;
        total_canceled_amount: number;
        total_canceled_tax_amount: number;
        tenders: {
            cardIn: string;
            cashIn: string;
            cardOut: string;
            cashOut: string;
            chequeIn: string;
            chequeOut: string;
            totalTenderAmount: string;
        };
        by_products: ReportLineItem[];
        by_services: ReportLineItem[];
        by_employee: ReportEmployeeItem[];
        by_sunbed: ReportLineItem[] | null;
        sunbed_total_amount: number | null;
        created_at: string;
        updated_at: string;
    };
}

// Fetch all Z Reports
export const fetchZReports = async (): Promise<ZReportsListResponse> => {
    const response = await api.get<ZReportsListResponse>("/admin/reports/z-report-table");
    return response.data;
};

// Fetch single Z Report by ID
export const fetchZReportDetail = async (id: string): Promise<ZReportDetailResponse> => {
    const response = await api.get<ZReportDetailResponse>(`/admin/reports/z-report/${id}`);
    return response.data;
};

// Generate new Z Report (requires startDate and endDate in YYYY-MM-DD)
export const generateZReport = async (params: {
    startDate: string;
    endDate: string;
}): Promise<{ message: string; report: any }> => {
    const response = await api.post<{ message: string; report: any }>("/admin/reports/z-report/generate", params);
    return response.data;
};

// Reset existing Z Report
export const resetZReport = async (id: number): Promise<{ message: string; report: any }> => {
    const response = await api.patch<{ message: string; report: any }>(`/admin/reports/z-report/${id}/reset`);
    return response.data;
};

/** Fetch Z Report as PDF blob for print/download. Backend must return Content-Type: application/pdf */
export const fetchZReportPdf = async (id: string): Promise<Blob> => {
    const response = await api.get(`/admin/reports/z-report/${id}/print`, {
        responseType: "blob",
        headers: { Accept: "application/pdf" },
    });
    return response.data as Blob;
};

// X Report Interfaces
export interface XReportResponse {
    report: {
        companyInfo: Array<{
            id: number;
            name: string;
            address: string;
        }>;
        begin: string;
        end: string;
        requester: number;
        closeOut: number;
        noOfSales: number;
        note: string;
        servicesTotalAmount: number | string;
        productsTotalAmount: number | string;
        sunbedTotalAmount?: number | string;
        totalSaleAmount: number | string;
        totalDiscount: number | string;
        totalTaxAmount: number | string;
        totalCanceledAmount: number | string;
        canceledTotalTaxAmount: number | string;
        tenders: {
            cashIn: number | string;
            cashOut: number | string;
            cardIn: number | string;
            cardOut: number | string;
            chequeIn: number | string;
            chequeOut: number | string;
            totalTenderAmount: number | string;
        };
        byService: ReportLineItem[];
        byProducts: ReportLineItem[];
        bySunbed?: ReportLineItem[];
        byEmployee: ReportEmployeeItem[];
        totalQuickPayment: number | string;
    };
}

// Fetch X Report
export const fetchXReport = async (): Promise<XReportResponse> => {
    const response = await api.get<XReportResponse>("/admin/reports/x-report");
    return response.data;
};

/** Fetch X Report as PDF blob for print/download. Backend must return Content-Type: application/pdf */
export const printXReport = async (): Promise<Blob> => {
    const response = await api.get("/admin/reports/x-report/print", {
        responseType: "blob",
        headers: { Accept: "application/pdf" },
    });
    return response.data as Blob;
};

// Sunbed Report API
export interface SunbedReportApiItem {
    customer_name: string;
    sunbed_package: string;
    sunbed_mins: string;
    amount: string;
    gateway: string;
    recharged_date: string;
}

export interface SunbedReportApiResponse {
    data: SunbedReportApiItem[];
}

export const fetchSunbedReport = async (): Promise<SunbedReportApiResponse> => {
    const response = await api.get<SunbedReportApiResponse>("/admin/reports/sunbedReportTable");
    return response.data;
};
